// Projects.js - קומפוננטת גלריית פרויקטים של לילך מורד

import React, { useState, useEffect, useRef } from "react";
import gsap from "gsap";
import styles from "./Projects.module.css";
import { projectsConfig } from "./ProjectsConfig";
import { loadThumbnail, loadProjectGallery, loadTaggedImages } from "./ImageLoader";

const WHATSAPP_NUMBER = "972505864422";

const Projects = () => {
  const sectionRef = useRef(null);
  const modalRef = useRef(null);
  const thumbnailRefs = useRef([]);
  const previewRef = useRef(null);

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectGallery, setProjectGallery] = useState([]);
  const [heroImages, setHeroImages] = useState([]);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [visibleThumbnails, setVisibleThumbnails] = useState(new Set());

  const getWhatsAppLink = (projectTitle = "") => {
    const message = projectTitle
      ? `היי לילך, אהבתי את הפרויקט ${projectTitle} ואשמח שנדבר`
      : "היי לילך, הגעתי מהאתר שלך ואשמח שנדבר";
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
  };

  // טעינת thumbnails
  useEffect(() => {
    const loadAllThumbnails = async () => {
      const loaded = await Promise.all(
        projectsConfig.map(async (config) => {
          try {
            const thumbnail = await loadThumbnail(
              config.folderName,
              config.thumbnailIndex
            );
            return { ...config, thumbnail };
          } catch (error) {
            console.error(`Failed to load thumbnail for ${config.title}:`, error);
            return { ...config, thumbnail: null };
          }
        })
      );
      setProjects(loaded);
    };
    loadAllThumbnails();
  }, []);

  // Intersection Observer לאנימציית כניסה
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setTimeout(() => {
              setVisibleThumbnails((prev) => new Set([...prev, index]));
            }, index * 100);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -50px 0px" }
    );

    thumbnailRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [projects]);

  // טעינת hero preview images (3 תמונות קטנות)
  const loadHeroImages = async (project) => {
    const indices = project.heroImages || [1, 2, 3];
    const images = [];
    for (const idx of indices) {
      try {
        const img = await loadThumbnail(project.folderName, idx);
        images.push(img);
      } catch (e) {
        console.warn(`Hero image ${idx} failed for ${project.folderName}`);
      }
    }
    setHeroImages(images);
  };

  // GSAP reveal animation for hero image
  useEffect(() => {
    if (heroImages.length > 0 && previewRef.current) {
      gsap.set(previewRef.current, {
        clipPath: "inset(0 0 100% 0)",
        opacity: 0,
      });

      gsap.to(previewRef.current, {
        clipPath: "inset(0 0 0% 0)",
        opacity: 1,
        duration: 1.1,
        ease: "power3.out",
        delay: 0.2,
      });
    }
  }, [heroImages]);

  // טעינת גלריה מלאה
  const loadFullGallery = async (project) => {
    try {
      const gallery = await loadProjectGallery(
        project.folderName,
        project.imageCount,
        project.excludeRange
      );
      // תמונות רגילות - ללא tag
      const mainImages = gallery.map((src) => ({ src, tag: null }));

      // תמונות tagged (הדמיות ראשוניות וכו')
      let tagged = [];
      if (project.taggedImages) {
        tagged = await loadTaggedImages(project.taggedImages);
      }

      // tagged images go at the end
      setProjectGallery([...mainImages, ...tagged]);
      setCurrentImageIndex(0);
    } catch (error) {
      console.error("Failed to load gallery:", error);
      setProjectGallery([]);
    }
  };

  const openProject = (project) => {
    setSelectedProject(project);
    setHeroImages([]);
    loadHeroImages(project);
    loadFullGallery(project);
    document.body.style.overflow = "hidden";
  };

  const closeProject = () => {
    document.body.style.overflow = "";
    setSelectedProject(null);
    setProjectGallery([]);
    setHeroImages([]);
    setIsGalleryOpen(false);
  };

  // Reset scroll on modal open
  useEffect(() => {
    if (selectedProject && modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  }, [selectedProject]);

  // Body scroll lock
  useEffect(() => {
    if (selectedProject) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [selectedProject]);

  const openGallery = (index = 0) => {
    setCurrentImageIndex(index);
    setIsGalleryOpen(true);
  };

  const closeGallery = () => setIsGalleryOpen(false);

  const nextImage = () => {
    if (projectGallery.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === projectGallery.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (projectGallery.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? projectGallery.length - 1 : prev - 1
      );
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isGalleryOpen) {
        if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
          e.key === "ArrowRight" ? prevImage() : nextImage();
        } else if (e.key === "Escape") {
          closeGallery();
        }
      } else if (selectedProject && e.key === "Escape") {
        closeProject();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isGalleryOpen, selectedProject, projectGallery]);

  // Cleanup
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <section className={styles.projectsSection} ref={sectionRef} id="projects">
      {/* Header */}
      <header className={styles.header}>
        <h2 className={styles.sectionTitle}>
          הפרויקטים <span className={styles.titleAccent}>שלנו</span>
        </h2>
        <p className={styles.sectionSubtitle}>
          כל בית מספר סיפור - הנה כמה מהסיפורים שלנו
        </p>
      </header>

      {/* Projects Grid */}
      <div className={styles.projectsGrid}>
        {projects.map((project, index) => (
          <article
            key={project.id}
            ref={(el) => (thumbnailRefs.current[index] = el)}
            data-index={index}
            className={`${styles.projectCard} ${
              index % 2 !== 0 ? styles.cardOffset : ""
            } ${visibleThumbnails.has(index) ? styles.visible : ""}`}
            onClick={() => openProject(project)}
            style={{ "--index": index }}
          >
            {/* Watermark number */}
            <span className={styles.cardNumber}>
              {String(index + 1).padStart(2, "0")}
            </span>

            <div className={styles.imageContainer}>
              {project.thumbnail && (
                <img
                  src={project.thumbnail}
                  alt={project.title}
                  className={styles.projectImage}
                  loading="lazy"
                />
              )}

              <div className={styles.cardOverlay}>
                <div className={styles.overlayContent}>
                  <span className={styles.viewLabel}>לצפייה בפרויקט</span>
                  <div className={styles.viewArrow}>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M7 17L17 7M17 7H7M17 7V17" />
                    </svg>
                  </div>
                </div>
              </div>

              <span className={styles.cardCategory}>{project.category}</span>
            </div>

            <div className={styles.cardInfo}>
              <h3 className={styles.cardTitle}>{project.title}</h3>
              <p className={styles.cardSubtitle}>{project.subtitle}</p>
              <span className={styles.cardCount}>
                {(project.excludeRange ? project.imageCount - (project.excludeRange[1] - project.excludeRange[0] + 1) : project.imageCount) + (project.taggedImages?.imageCount || 0)} תמונות
              </span>
            </div>
          </article>
        ))}
      </div>

      {/* CTA */}
      <div className={styles.sectionCta}>
        <a
          href={getWhatsAppLink()}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.ctaButton}
        >
          <span className={styles.ctaText}>אהבתי את הסגנון, בואי נדבר!</span>
          <svg
            className={styles.ctaArrow}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
        </a>
      </div>

      {/* ════════ PROJECT MODAL ════════ */}
      {selectedProject && (
        <div className={styles.modalBackdrop} ref={modalRef}>
          <div className={styles.modal}>
            {/* Close */}
            <button
              className={styles.closeBtn}
              onClick={closeProject}
              aria-label="סגור"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>

            <div className={styles.modalContent}>
              {/* Hero Image */}
              {heroImages.length > 0 && (
                <div
                  className={styles.modalHero}
                  ref={previewRef}
                  data-preview-item
                  onClick={() => {
                    const heroIdx = selectedProject.heroImages?.[0] - 1;
                    if (heroIdx >= 0) openGallery(heroIdx);
                  }}
                >
                  <img src={heroImages[0]} alt={selectedProject.title} />
                  <div className={styles.heroOverlay}>
                    <span className={styles.modalCategory}>
                      {selectedProject.category}
                    </span>
                    <h2 className={styles.modalTitle}>{selectedProject.title}</h2>
                    <p className={styles.modalSubtitle}>
                      {selectedProject.subtitle}
                    </p>
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedProject.description && (
                <div className={styles.descriptionSection}>
                  <div className={styles.descriptionText}>
                    {selectedProject.description
                      .split("\n")
                      .map((paragraph, idx) => (
                        <p key={idx}>{paragraph}</p>
                      ))}
                  </div>
                </div>
              )}

              {/* Gallery Grid */}
              <div className={styles.gallerySection}>
                <div className={styles.galleryGrid}>
                  {projectGallery.map((item, idx) => (
                    <div
                      key={idx}
                      className={styles.galleryThumb}
                      onClick={() => openGallery(idx)}
                    >
                      <img
                        src={item.src || item}
                        alt={`${selectedProject.title} ${idx + 1}`}
                        loading="lazy"
                      />
                      {item.tag && (
                        <div className={styles.imageTag}>
                          <span>{item.tag}</span>
                        </div>
                      )}
                      <div className={styles.thumbHover}>
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        >
                          <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Modal CTA */}
                <div className={styles.modalCta}>
                  <a
                    href={getWhatsAppLink(selectedProject.title)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.modalCtaButton}
                  >
                    <span>אהבתי את הסגנון, בואי נדבר!</span>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M19 12H5M12 5l-7 7 7 7" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════ LIGHTBOX ════════ */}
      {isGalleryOpen && selectedProject && projectGallery.length > 0 && (
        <div className={styles.lightbox}>
          <button
            className={styles.lightboxClose}
            onClick={closeGallery}
            aria-label="סגור גלריה"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <div className={styles.lightboxViewer}>
            <img
              src={projectGallery[currentImageIndex]?.src || projectGallery[currentImageIndex]}
              alt=""
              className={styles.lightboxImage}
            />
            {projectGallery[currentImageIndex]?.tag && (
              <div className={styles.lightboxTag}>
                <span>{projectGallery[currentImageIndex].tag}</span>
              </div>
            )}
          </div>

          <button
            className={`${styles.lightboxNav} ${styles.navPrev}`}
            onClick={prevImage}
            aria-label="תמונה קודמת"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
          <button
            className={`${styles.lightboxNav} ${styles.navNext}`}
            onClick={nextImage}
            aria-label="תמונה הבאה"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <div className={styles.lightboxProgress}>
            <span className={styles.progressCount}>
              {String(currentImageIndex + 1).padStart(2, "0")} / {String(projectGallery.length).padStart(2, "0")}
            </span>
          </div>

          <div className={styles.lightboxStrip}>
            {projectGallery.map((item, idx) => (
              <div
                key={idx}
                className={`${styles.stripThumb} ${idx === currentImageIndex ? styles.stripActive : ""}`}
                onClick={() => setCurrentImageIndex(idx)}
              >
                <img src={item.src || item} alt="" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default Projects;
